import get from 'lodash/get';
import set from 'lodash/set';
import pick from 'lodash/pick';
import setWith from 'lodash/setWith';
import findKey from 'lodash/findKey';
import toPairs from 'lodash/toPairs';
import {call, put, select, delay, takeEvery, takeLatest} from 'redux-saga/effects';

import {changeFields} from 'client/common/store/reducers/context';
import {setErrors} from 'client/common/store/reducers/errors/brandlift';
import {scrollToError} from 'client/common/store/actions/scroll';
import * as iframeActions from 'client/common/store/actions/iframe';
import * as metrikaActions from 'client/common/store/actions/metrika';
import * as questionsActions from 'client/common/store/actions/questions';
import * as surveyActions from 'client/common/store/actions/survey';

import {
    BrandTypes,
    QuicksurveyContext,
    BrandliftContext
} from 'client/common/store/reducers/context/types';

import {getContext, getIsBrandliftContextChanged} from 'client/common/store/selectors/context';
import {getSlug} from 'client/common/store/selectors/survey';

import {BRANDLIFT_METRIC_COUNT, FIELDS} from 'client/common/types/brandlift/consts';
import {Metric, CreateFormFields} from 'client/common/types/brandlift/types';

import {AudienceType, BrandliftGoal, SurveySlug} from 'client/common/types';

import history, {createUrl} from 'client/common/utils/history';
import {
    getBrandliftQuestionLabelMaxLength,
    getBrandliftOptionsMaxLength
} from 'client/common/utils/brandlift-limits';
import {
    getRevision,
    updateSurveyBulk,
    getSurvey,
    createSurvey as createSurveyRequest
} from 'client/common/utils/api-client/constructor';
import {getActiveRevision, getCurrentRevision} from 'client/common/utils/revision';

import i18n from 'assets/i18n/brandlift';
import I18N from 'assets/i18n/brandlift-project';

import {GetSurveyResponse, RevisionResponse} from 'client/common/utils/api-client/types';
import {transformLegacyBrandliftBox} from 'client/common/utils/brandlift';
import {logError} from '@yandex-int/pythia-libs';

const translations = I18N.common.saga.brandlift.survey;

export function* fetchQuestions(slug: string, revisionIndex: number) {
    const res: RevisionResponse = yield call(getRevision, {
        slug,
        revisionIndex,
        apiLocation: 'brandlift'
    });

    if (res.status === 200 && res.data) {
        const {
            options: brandLiftOptions,
            questions: brandLiftQuestions,
            placeholders = {},
            version
        } = get(res.data, ['customStatConfig', 'detachedBrandLift', 'config'], {});

        const questions = get(res.data, ['questions'], null);

        if (!brandLiftOptions || !brandLiftQuestions || !questions) {
            throw new Error('Invalid survey revision config');
        }

        const questionsConfig = Object.keys(brandLiftQuestions).reduce((rev: any, metrics) => {
            // @ts-ignore
            const {label, options} = questions[brandLiftQuestions[metrics]];
            const placeholder = placeholders[metrics];

            rev[metrics] = {
                label,
                placeholder,
                options
            };

            return rev;
        }, {});

        yield put(changeFields({version: version === undefined ? 0 : version}));
        yield put(questionsActions.setQuestions(questionsConfig));
    } else {
        yield call(logError, {message: 'Can not fetch revision', additional: {data: res.data}});
    }
}

export function* handleFetchSurveySuccess(data: any): any {
    const {slug, state, updatedAt, revisions, box} = data;

    if (box.name !== 'detachedBrandLift') {
        return yield call(logError, {
            // eslint-disable-next-line no-cyrillic-string/no-cyrillic-string
            message: 'Неправильный тип коробки',
            additional: {box: box.name}
        });
    }

    // TODO убрать, когда не останется старых коробок с customMetrics не в контексте
    const {context} = transformLegacyBrandliftBox(box);

    yield put(
        surveyActions.setSurvey({
            state,
            updatedAt,
            // @ts-ignore
            context: {
                brandType: BrandTypes.PRODUCT,
                ...context
            }
        })
    );

    yield put(
        // @ts-ignore
        changeFields({
            brandType: BrandTypes.PRODUCT,
            ...context
        })
    );

    const activeRevision = yield call(getActiveRevision, revisions);
    const currentRevision = yield call(getCurrentRevision, revisions);

    const revision = activeRevision || currentRevision;

    if (revision) {
        yield call(fetchQuestions, slug, revision.id);
    }
}

export function* handleFetchSurveyFailure(res: any) {
    yield call(logError, {message: 'Can not fetch survey', additional: {data: res?.data}});
}

export function* fetchSurvey() {
    const slug: SurveySlug = yield select(getSlug);

    if (!slug) {
        return;
    }

    const res: GetSurveyResponse = yield call(getSurvey, {
        slug,
        apiLocation: 'brandlift'
    });

    if (res.status === 200 && res.data && res.data.box) {
        yield call(handleFetchSurveySuccess, res.data);
    } else {
        yield call(handleFetchSurveyFailure, res);
    }
}

const VALIDATE_ERROR = {
    minQuantity: i18n.validationError.minQuantity,
    empty: i18n.validationError.empty,
    maxLength: i18n.validationError.maxLength,
    empty2: i18n.validationError.empty2,
    noFile: i18n.validationError.noFile
};

// eslint-disable-next-line max-statements
function validateMultipleField(key: keyof CreateFormFields, value: string[]) {
    if (!Array.isArray(value)) {
        const message = VALIDATE_ERROR.empty;

        // показываем ошибку только у первых двух полей
        return [{message}, {message}];
    }

    const valuesCount = value.filter(v => Boolean(v)).length;
    const {minQuantity} = FIELDS[key];
    const maxLength = getBrandliftOptionsMaxLength();

    if (minQuantity && valuesCount === 0) {
        const message = VALIDATE_ERROR.minQuantity.replace('%s', minQuantity.toString());

        // показываем ошибку только у первых двух полей
        return [{message}, {message}];
    }

    if (minQuantity && valuesCount < minQuantity) {
        // показываем ошибку только у второго поля
        return [
            {},
            {
                message: VALIDATE_ERROR.minQuantity.replace('%s', minQuantity.toString())
            }
        ];
    }

    const errorsWithIndex: {number?: {message: string}} = {};

    for (const [index, str] of value.entries()) {
        if (str.length > maxLength) {
            // @ts-ignore
            errorsWithIndex[index] = {
                message: VALIDATE_ERROR.maxLength.replace('%s', maxLength.toString())
            };
        }
    }

    if (Object.keys(errorsWithIndex).length) {
        return errorsWithIndex;
    }
}

function validateRequiredField(key: keyof CreateFormFields, value: any) {
    if (!value && FIELDS[key].isRequired) {
        return VALIDATE_ERROR.empty;
    }

    if (value) {
        let maxLength = 0;

        if (key === 'adMessage' || key === 'brand') {
            maxLength = getBrandliftOptionsMaxLength();
        }

        if (maxLength && value.length > maxLength) {
            return VALIDATE_ERROR.maxLength.replace('%s', maxLength.toString());
        }
    }
}

function validateCustomFields(context: BrandliftContext) {
    const {
        metricLabels = {},
        metricOptions = {},
        metricPlaceholders = {},
        brandType = BrandTypes.PRODUCT
    } = context;

    const errors: any = {};
    const metricLabelMaxLength = getBrandliftQuestionLabelMaxLength(); // + brand и Ничего из перечисленного

    toPairs(metricLabels).forEach(([metricName, metricLabel]) => {
        if (!metricLabel) {
            set(errors, ['customFormulations', metricName, 'label'], VALIDATE_ERROR.empty);
        }
    });

    const getQuestionLabel = (metric: Metric) => {
        let metricLabel;

        switch (metric) {
            case 'purchaseIntent':
                metricLabel =
                    brandType === BrandTypes.SERVICE
                        ? i18n.brandliftPage.questionLabel2.purchaseIntentForService
                        : i18n.brandliftPage.questionLabel2.purchaseIntentForProduct;
                break;
            default:
                metricLabel = i18n.brandliftPage.questionLabel2[metric];
        }

        return metricLabel;
    };

    toPairs(metricPlaceholders).forEach(([metricName, metricPlaceholder]) => {
        const placeholderMaxLength =
            metricLabelMaxLength - getQuestionLabel(metricName as Metric).length + 2;

        if (!metricPlaceholder) {
            set(errors, ['customFormulations', metricName, 'placeholder'], VALIDATE_ERROR.empty2);
        }

        if (metricPlaceholder.length > placeholderMaxLength) {
            set(
                errors,
                ['customFormulations', metricName, 'placeholder'],
                VALIDATE_ERROR.maxLength.replace('%s', placeholderMaxLength.toString())
            );
        }
    });

    toPairs(metricOptions).forEach(([metricName, options = []]) => {
        options.forEach((option, index) => {
            if (!option) {
                setWith(
                    errors,
                    ['customFormulations', metricName, 'options', index.toString()],
                    VALIDATE_ERROR.empty,
                    Object
                );
            }
        });
    });

    return errors;
}

function validateDefaultFields(context: BrandliftContext) {
    const fields = pick(context, Object.keys(FIELDS));

    const errors = Object.keys(fields).reduce((acc: any, key: keyof CreateFormFields) => {
        const value = fields[key];

        switch (key) {
            case 'brands':
            case 'adMessages':
                acc[key] = validateMultipleField(key, value as string[]);

                break;
            default:
                acc[key] = validateRequiredField(key, value);
        }

        return acc;
    }, {});

    return errors;
}

function validateLogos(context: BrandliftContext): any {
    const {brand, brands, logos} = context;
    const all_brands = [brand].concat(brands);

    const errors = all_brands.reduce(
        (acc: any, currentBrand: string, idx: number) => {
            if (
                currentBrand &&
                currentBrand.length > 0 &&
                (logos[idx] === undefined || logos[idx]?.url === undefined)
            ) {
                acc.logos[idx] = VALIDATE_ERROR.noFile;
            }

            return acc;
        },
        {logos: []}
    );

    return errors.logos.length > 0 ? errors : {};
}

function validate(context: BrandliftContext) {
    const {isCustomFormulations, logos} = context;

    let errors = validateDefaultFields(context);

    if (isCustomFormulations) {
        errors = {...errors, ...validateCustomFields(context)};
    }

    if (logos.length > 0) {
        errors = {...errors, ...validateLogos(context)};
    }

    return errors;
}

export function getDefaultMetricLabels(
    brandType: string,
    customMetrics: Metric[],
    version: number
) {
    return customMetrics.reduce((acc: any, metric) => {
        let metricLabel = '';

        switch (metric) {
            case 'purchaseIntent':
                metricLabel =
                    brandType === BrandTypes.SERVICE
                        ? i18n.brandliftPage.questionLabel2.purchaseIntentForService
                        : i18n.brandliftPage.questionLabel2.purchaseIntentForProduct;
                break;
            case 'productConsideration':
                metricLabel =
                    version < 2
                        ? i18n.brandliftPage.questionLabel2.productConsideration
                        : i18n.brandliftPage.questionLabel2.productConsideration2;
                break;
            default:
                metricLabel = i18n.brandliftPage.questionLabel2[metric];
        }

        acc[metric] = metricLabel;

        return acc;
    }, {});
}

export function* collectCreateData() {
    const {
        brand,
        brands,
        brandType = BrandTypes.PRODUCT,
        industry,
        adMessage,
        adMessages,
        customMetrics,
        metricOptions,
        metricPlaceholders,
        isCustomFormulations,
        version,
        logos
    } = yield select(getContext);

    const metricLabels: any = {};
    const qustionLabels = i18n.brandliftPage.questionLabel2;

    Object.keys(metricPlaceholders).forEach((metric: Metric) => {
        switch (metric) {
            case 'purchaseIntent':
                metricLabels[metric] =
                    brandType === BrandTypes.SERVICE
                        ? qustionLabels.purchaseIntentForService.replace(
                              '%s',
                              metricPlaceholders[metric]
                          )
                        : qustionLabels.purchaseIntentForProduct.replace(
                              '%s',
                              metricPlaceholders[metric]
                          );
                break;
            case 'productConsideration':
                metricLabels[metric] =
                    version < 2
                        ? qustionLabels.productConsideration.replace(
                              '%s',
                              metricPlaceholders[metric]
                          )
                        : qustionLabels.productConsideration2.replace(
                              '%s',
                              metricPlaceholders[metric]
                          );
                break;
            default:
                metricLabels[metric] = qustionLabels[metric].replace(
                    '%s',
                    metricPlaceholders[metric]
                );
        }
    });

    const context: BrandliftContext = {
        brand,
        brands: Array.isArray(brands) && brands.filter((b: string) => b.length > 0),
        industry,
        ...(customMetrics?.includes('adMessageRecall') && {
            adMessage,
            adMessages:
                Array.isArray(adMessages) && adMessages.filter((msg: string) => msg.length > 0)
        }),
        ...(customMetrics?.some((metric: string) =>
            ['purchaseIntent', 'productConsideration'].includes(metric)
        ) && {
            brandType
        }),
        customMetrics,
        metricLabels,
        metricPlaceholders,
        metricOptions,
        isCustomFormulations,
        defaultMetricLabels: getDefaultMetricLabels(brandType, customMetrics || [], version),
        version,
        logos,
        strategy: 'invitation'
    };

    return context;
}

export function* prepareSurveyData(): any {
    const context = yield call(collectCreateData);

    const validateErrors = yield call(validate, context);

    if (!context.customMetrics?.length || context.customMetrics.length < BRANDLIFT_METRIC_COUNT) {
        validateErrors.customMetrics = VALIDATE_ERROR.minQuantity.replace(
            '%s',
            BRANDLIFT_METRIC_COUNT.toString()
        );
    }

    const hasErrors = Boolean(validateErrors) && findKey(validateErrors, value => Boolean(value));

    if (hasErrors) {
        return {
            errors: validateErrors
        };
    }

    return {context};
}

// eslint-disable-next-line max-statements
export function* createSurvey(action: ReturnType<typeof surveyActions.createSurvey>): any {
    // Нас показывают в iframe. Посылают мессадж на создание опроса со специальным id.
    const reqId = get(action, ['payload', 'reqId']);

    try {
        const slug: SurveySlug = yield select(getSlug);

        if (slug) {
            return yield put(surveyActions.updateSurvey({reqId}));
        }

        const {errors, context} = yield call(prepareSurveyData);

        if (errors) {
            yield put(setErrors(errors));

            yield put(
                iframeActions.sendMessage({
                    status: 'validation-error',
                    reqId
                })
            );

            yield delay(1);

            return yield put(scrollToError());
        }

        const res = yield call(createSurveyRequest, {
            survey: {
                audience: {type: AudienceType.Quotas, size: 10000},
                name: translations.surveyName.replace('%s', new Date().toLocaleDateString()),
                box: {
                    name: 'detachedBrandLift',
                    context
                }
            },
            apiLocation: 'brandlift'
        });

        if (res.status === 201) {
            yield call(handleCreateSurveySuccess, res.data.slug, reqId);
        } else {
            yield call(handleCreateSurveyFailure, res, reqId);
        }
    } catch (e) {
        yield put(
            iframeActions.sendMessage({
                status: 'fatal-error',
                reqId
            })
        );
    }
}

export function* handleCreateSurveySuccess(slug: string, reqId?: string) {
    yield call(
        history.push.bind(history),
        createUrl({link: `/brandlift/survey/${slug}`, relative: true})
    );

    yield put(
        surveyActions.setSurvey({
            slug
        })
    );

    yield put(
        iframeActions.sendMessage({
            status: 'ok',
            reqId,
            result: {
                slug
            }
        })
    );

    yield put(metrikaActions.sendGoals([BrandliftGoal.CreateSurvey]));
}

export function* handleCreateSurveyFailure(res: any, reqId?: string) {
    yield call(logError, {message: 'Error while creating survey', additional: {data: res.data}});

    yield put(
        iframeActions.sendMessage({
            status: 'fatal-error',
            reqId
        })
    );
}

// eslint-disable-next-line max-statements
export function* updateSurvey(action: ReturnType<typeof surveyActions.updateSurvey>): any {
    const reqId = get(action, ['payload', 'reqId']);

    try {
        const slug: SurveySlug = yield select(getSlug);

        const {errors, context} = yield call(prepareSurveyData);

        if (errors) {
            yield put(setErrors(errors));

            yield put(
                iframeActions.sendMessage({
                    status: 'validation-error',
                    reqId
                })
            );

            yield delay(1);

            return yield put(scrollToError());
        }

        const res = yield call(updateSurveyBulk, {
            slug,
            data: {
                box: {
                    name: 'detachedBrandLift',
                    context
                }
            },
            apiLocation: 'brandlift'
        });

        if (res.status === 204) {
            yield call(handleUpdateSurveySuccess, context, reqId);
        } else {
            yield call(handleUpdateSurveyFailure, res, reqId);
        }
    } catch (e) {
        yield put(
            iframeActions.sendMessage({
                status: 'fatal-error',
                reqId
            })
        );
    }
}

export function* handleUpdateSurveySuccess(context: QuicksurveyContext, reqId?: string) {
    const slug: string = yield select(getSlug);

    yield put(
        surveyActions.setSurvey({
            context: {
                ...context
            }
        })
    );

    yield put(
        changeFields({
            ...context
        })
    );

    yield put(
        iframeActions.sendMessage({
            status: 'ok',
            reqId,
            result: {
                slug
            }
        })
    );

    yield put(metrikaActions.sendGoals([BrandliftGoal.EditSurvey]));
}

export function* handleUpdateSurveyFailure(res: any, reqId?: string) {
    yield call(logError, {message: 'Error while updating survey', additional: {data: res.data}});

    yield put(
        iframeActions.sendMessage({
            status: 'fatal-error',
            reqId
        })
    );
}

export function* frameCheckHasChanges(action: ReturnType<typeof surveyActions.hasChanges>) {
    const reqId = get(action, ['payload', 'reqId']);
    const hasChanges: boolean = yield select(getIsBrandliftContextChanged);

    yield put(
        iframeActions.sendMessage({
            status: 'ok',
            reqId,
            result: {
                hasChanges
            }
        })
    );
}

export default function* router() {
    yield takeLatest(surveyActions.fetchSurvey.type, fetchSurvey);
    yield takeEvery(surveyActions.createSurvey.type, createSurvey);
    yield takeEvery(surveyActions.updateSurvey.type, updateSurvey);
    yield takeEvery(surveyActions.hasChanges.type, frameCheckHasChanges);
}
